import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const GET_EXAM_PERIODS_URL = `${API_URL}/Custom/GetExamPeriods`;
const GET_BUILDINGS_URL = `${API_URL}/Custom/GetBuildings`;
const GET_ASSIGNABLE_USERS_URL = `${API_URL}/Custom/GetAssignableUsers`;
const POST_ASSIGN_BUILDING_URL = `${API_URL}/Custom/AssignBuildingStaff`;
const GET_BUILDING_ASSIGNMENTS_URL = `${API_URL}/Custom/GetBuildingAssignments`;

export default function BuildingAssignmentManager() {
  const [examPeriods, setExamPeriods] = useState([]);
  const [selectedExamPeriod, setSelectedExamPeriod] = useState(null);

  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState(null);

  const [staff, setStaff] = useState([]);
  const [supervisorIds, setSupervisorIds] = useState([]);
  const [backupIds, setBackupIds] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    axios
      .get(GET_EXAM_PERIODS_URL)
      .then(res => setExamPeriods(res.data || []))
      .catch(err => console.error(err));

    axios
      .get(GET_BUILDINGS_URL)
      .then(res => setBuildings(res.data || []))
      .catch(err => console.error(err));

    axios
      .get(GET_ASSIGNABLE_USERS_URL)
      .then(res => setStaff(res.data || []))
      .catch(err => console.error(err));
  }, []);

  const sortedStaff = useMemo(
    () => [...staff].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [staff]
  );

  const handleSupervisorChange = event => {
    const selectedValues = Array.from(event.target.selectedOptions, option => Number(option.value)).filter(Boolean);
    const distinctSupervisors = Array.from(new Set(selectedValues));
    setSupervisorIds(distinctSupervisors);
    if (distinctSupervisors.length > 0) {
      setBackupIds(prev => prev.filter(id => !distinctSupervisors.includes(id)));
    }
    setStatus('');
  };

  const handleBackupChange = event => {
    const selectedValues = Array.from(event.target.selectedOptions, option => Number(option.value));
    setBackupIds(selectedValues.filter(id => id && !supervisorIds.includes(id)));
    setStatus('');
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!selectedExamPeriod || !selectedBuilding || supervisorIds.length === 0) {
      return;
    }

    setIsSaving(true);
    setIsClearing(false);
    setStatus('Bina görevlileri atanıyor...');

    try {
      await axios.post(POST_ASSIGN_BUILDING_URL, {
        examPeriodId: selectedExamPeriod,
        buildingId: selectedBuilding,
        supervisorIds: supervisorIds,
        backupInvigilatorIds: backupIds
      });

      setStatus('Bina görevlileri güncellendi.');
      setIsSaving(false);
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data || error.message;
      setStatus(`Hata: ${message}`);
      setIsSaving(false);
    }
  };

  const handleClearAssignments = async () => {
    if (!selectedExamPeriod || !selectedBuilding) {
      return;
    }

    setIsSaving(true);
    setIsClearing(true);
    setStatus('Bina görevlileri kaldırılıyor...');

    try {
      await axios.post(POST_ASSIGN_BUILDING_URL, {
        examPeriodId: selectedExamPeriod,
        buildingId: selectedBuilding,
        supervisorIds: [],
        backupInvigilatorIds: []
      });

      setSupervisorIds([]);
      setBackupIds([]);
      setStatus('Bina görevlileri kaldırıldı.');
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data || error.message;
      setStatus(`Hata: ${message}`);
    } finally {
      setIsSaving(false);
      setIsClearing(false);
    }
  };

  const resetSelections = () => {
    setSupervisorIds([]);
    setBackupIds([]);
    setStatus('');
  };

  useEffect(() => {
    if (!selectedExamPeriod || !selectedBuilding) {
      resetSelections();
      return;
    }

    let isActive = true;
    setIsLoadingAssignments(true);
    setSupervisorIds([]);
    setBackupIds([]);
    if (!isSaving) {
      setStatus('Görevli bilgileri yükleniyor...');
    }

    axios
      .get(
        `${GET_BUILDING_ASSIGNMENTS_URL}?examPeriodId=${selectedExamPeriod}&buildingId=${selectedBuilding}`
      )
      .then(response => {
        if (!isActive) {
          return;
        }

        const data = response?.data || {};
        const supervisorSource = Array.isArray(data.SupervisorIds)
          ? data.SupervisorIds
          : Array.isArray(data.supervisorIds)
          ? data.supervisorIds
          : [];
        const supervisors = Array.from(new Set(supervisorSource.map(Number).filter(Boolean)));
        const backupSource = Array.isArray(data.BackupIds)
          ? data.BackupIds
          : Array.isArray(data.backupIds)
          ? data.backupIds
          : [];
        const backups = Array.from(new Set(backupSource.map(Number).filter(Boolean))).filter(
          id => !supervisors.includes(id)
        );

        setSupervisorIds(supervisors);
        setBackupIds(backups);

        if (!isSaving) {
          if (supervisors.length === 0 && backups.length === 0) {
            setStatus('Bu bina ve dönem için kayıtlı bina görevlisi bulunamadı.');
          } else {
            setStatus('Seçilen bina ve döneme ait mevcut görevliler yüklendi.');
          }
        }
      })
      .catch(error => {
        if (!isActive) {
          return;
        }

        if (!isSaving) {
          const message =
            error?.response?.data?.message || error?.response?.data || error?.message || 'Görevli bilgileri alınamadı.';
          setStatus(`Hata: ${message}`);
        }
        setSupervisorIds([]);
        setBackupIds([]);
      })
      .finally(() => {
        if (isActive) {
          setIsLoadingAssignments(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [selectedExamPeriod, selectedBuilding]);

  const staffById = useMemo(() => {
    const map = new Map();
    staff.forEach(person => {
      if (person && person.id) {
        map.set(person.id, person);
      }
    });
    return map;
  }, [staff]);

  const formatPerson = useCallback(
    id => {
      const person = staffById.get(id);
      if (!person) {
        return `#${id}`;
      }
      const title = person.title ? ` (${person.title})` : '';
      return `${person.name}${title}`;
    },
    [staffById]
  );

  const supervisorSummary = useMemo(
    () => (supervisorIds.length > 0 ? supervisorIds.map(formatPerson).join(', ') : 'Seçilmedi'),
    [supervisorIds, formatPerson]
  );

  const backupSummary = useMemo(
    () => (backupIds.length > 0 ? backupIds.map(formatPerson).join(', ') : 'Seçilmedi'),
    [backupIds, formatPerson]
  );

  const canSubmit = Boolean(
    selectedExamPeriod && selectedBuilding && supervisorIds.length > 0 && !isSaving && !isLoadingAssignments
  );

  const canClear = Boolean(
    selectedExamPeriod &&
      selectedBuilding &&
      !isSaving &&
      !isLoadingAssignments
  );

  return (
    <div className="card p-4">
      <h2 className="mb-4">Bina Görevli Ataması</h2>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Sınav Dönemi</label>
              <select
                className="form-select"
                value={selectedExamPeriod || ''}
                onChange={event => setSelectedExamPeriod(event.target.value ? Number(event.target.value) : null)}
              >
                <option value="" disabled>
                  Dönem seçiniz
                </option>
                {examPeriods.map(period => (
                  <option key={period.id} value={period.id}>
                    {period.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-md-4">
            <div className="mb-3">
              <label className="form-label">Bina</label>
              <select
                className="form-select"
                value={selectedBuilding || ''}
                onChange={event => setSelectedBuilding(event.target.value ? Number(event.target.value) : null)}
              >
                <option value="" disabled>
                  Bina seçiniz
                </option>
                {buildings.map(building => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="col-md-4 d-flex align-items-end">
            <button
              type="button"
              className="btn btn-light ms-md-auto"
              onClick={resetSelections}
              disabled={supervisorIds.length === 0 && backupIds.length === 0}
            >
              Seçimleri Temizle
            </button>
          </div>
        </div>

        {selectedExamPeriod && selectedBuilding && (
          <div className="row">
            <div className="col-12">
              <div className="alert alert-info" role="status">
                <div className="fw-semibold mb-2">Seçili Görevli Özeti</div>
                <div>
                  <strong>Bina Sınav Sorumlusu:</strong> {supervisorSummary}
                </div>
                <div>
                  <strong>Yedek Gözetmenler:</strong> {backupSummary}
                </div>
                {isLoadingAssignments && <div className="mt-2">Görevli bilgileri yükleniyor...</div>}
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Bina Sınav Sorumlusu</label>
              <select
                multiple
                className="form-select"
                value={supervisorIds.map(String)}
                onChange={handleSupervisorChange}
                size={8}
              >
                {sortedStaff.map(person => (
                  <option key={person.id} value={person.id}>
                    {person.name} {person.title ? `(${person.title})` : ''}
                  </option>
                ))}
              </select>
              <div className="form-text">Bir veya daha fazla bina sınav sorumlusu seçebilirsiniz.</div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">Yedek Gözetmenler</label>
              <select
                multiple
                className="form-select"
                value={backupIds.map(String)}
                onChange={handleBackupChange}
                size={8}
              >
                {sortedStaff.map(person => (
                  <option key={person.id} value={person.id} disabled={supervisorIds.includes(person.id)}>
                    {person.name} {person.title ? `(${person.title})` : ''}
                  </option>
                ))}
              </select>
              <div className="form-text">İstediğiniz kadar yedek gözetmen seçebilirsiniz.</div>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center">
          <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
            {isSaving && !isClearing ? 'Kaydediliyor...' : 'Görevlendir'}
          </button>
          <button
            type="button"
            className="btn btn-outline-danger ms-2"
            onClick={handleClearAssignments}
            disabled={!canClear}
          >
            {isSaving && isClearing ? 'Siliniyor...' : 'Görevlileri Sil'}
          </button>
          {status && <span className="ms-3">{status}</span>}
        </div>
      </form>
    </div>
  );
}
